// @flow
import { useState, useEffect, useCallback } from 'react';

/**
 * navigator.onLine is a hint and can be stale after hard reloads/offline toggles.
 * We combine it with a lightweight network probe to determine actual reachability.
 */
const CONNECTIVITY_TIMEOUT_MS = 2000;

const probeConnectivity = async (): Promise<boolean> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    CONNECTIVITY_TIMEOUT_MS,
  );

  try {
    // Any HTTP response (including 404) means the network path is reachable.
    await fetch(`/_connectivity_check?t=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Returns the current online/offline status and subscribes to changes.
 * Uses both browser events and active probe checks for reliability.
 */
const useOfflineStatus = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const refreshConnectivity = useCallback(async () => {
    // Fast path: browser says offline.
    if (!navigator.onLine) {
      setIsOffline(true);
      return;
    }

    // Validate actual network reachability.
    const isReachable = await probeConnectivity();
    setIsOffline(!isReachable);
  }, []);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => {
      // Re-check on online event because navigator.onLine can be stale.
      refreshConnectivity();
    };

    const handleVisibilityOrFocus = () => {
      // Re-validate when tab becomes active.
      refreshConnectivity();
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    // Initial validation on mount.
    refreshConnectivity();

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [refreshConnectivity]);

  return { isOffline, isOnline: !isOffline };
};

export default useOfflineStatus;
